/**
 * Kivi's poses and the thirteen app states, ported from the Kivi guide
 * (design/explorations/kivi/kivi-in-recordos.html): pose, breath, blink, sn and
 * blendPose from the model, the states from the states module (the shared
 * STATES entries with the module's retunes, plus touch and quiet from the
 * button's ST). Every function is a worklet.
 */
import { TAU } from './geometry';
import { clamp, easeIO, easeOut, lerp } from './math';
import type { KiviCut, KiviEyes, KiviFx, KiviLine, KiviPivot, KiviPose } from './types';

export { clamp, easeIO, easeOut };

/** What a pose is built from: every field optional, as the page's pose(o). */
export type KiviPoseInput = {
  readonly rot?: number;
  readonly piv?: KiviPivot;
  readonly tx?: number;
  readonly ty?: number;
  readonly sx?: number;
  readonly sy?: number;
  readonly tint?: number;
  readonly eyes?: KiviEyes;
  readonly line?: KiviLine;
  readonly glow?: number;
  readonly fx?: KiviFx | null;
};

export function pose(o: KiviPoseInput): KiviPose {
  'worklet';
  return {
    rot: o.rot || 0,
    piv: o.piv || 'front',
    tx: o.tx || 0,
    ty: o.ty || 0,
    sx: o.sx || 1,
    sy: o.sy || 1,
    tint: o.tint || 0,
    eyes: o.eyes || { mode: 'lid' },
    line: o.line || {},
    glow: o.glow || 0,
    fx: o.fx || null,
  };
}

/** A sine: a times sin(2π f t + ph). */
export function sn(t: number, f: number, a?: number, ph?: number): number {
  'worklet';
  return (a == null ? 1 : a) * Math.sin(TAU * f * t + (ph || 0));
}

/** An open eye's openness: 1, closing and opening over 160 ms once a period. */
export function blink(t: number, period?: number, off?: number): number {
  'worklet';
  const p = period || 4.3;
  const x = (((t + (off || 0)) % p) + p) % p;
  return x < 0.16 ? Math.abs(x - 0.08) / 0.08 : 1;
}

/** A slow breath: the disc taller and a little narrower, about 1% at f Hz. */
export function breath(P: KiviPose, t: number, f?: number, a?: number): KiviPose {
  'worklet';
  const b = Math.sin(TAU * (f || 0.24) * t) * (a == null ? 0.012 : a);
  return { ...P, sy: (P.sy || 1) * (1 + b), sx: (P.sx || 1) * (1 - b * 0.45) };
}

/**
 * Blend two poses over a state change: positions ease, pivots hand over
 * through upright, eyes close briefly through the switch.
 */
export function blendPose(A: KiviPose, B: KiviPose, u: number): KiviPose {
  'worklet';
  if (u >= 1) return B;
  if (u <= 0) return A;
  const samePiv = (A.piv || 'front') === (B.piv || 'front');
  const piv = samePiv ? B.piv : u < 0.5 ? A.piv : B.piv;
  const rot = samePiv
    ? lerp(A.rot || 0, B.rot || 0, u)
    : u < 0.5
      ? lerp(A.rot || 0, 0, u * 2)
      : lerp(0, B.rot || 0, (u - 0.5) * 2);
  const ea = A.eyes || {};
  const eb = B.eyes || {};
  const la = ea.look || [0, 0];
  const lb = eb.look || [0, 0];
  const from = u < 0.5 ? ea : eb;
  const eyes: KiviEyes = {
    ...from,
    look: [lerp(la[0], lb[0], u), lerp(la[1], lb[1], u)],
    open: (from.open == null ? 1 : from.open) * Math.min(1, Math.abs(u - 0.5) * 2.6 + 0.05),
  };
  const a = A.line || {};
  const b = B.line || {};
  let line: KiviLine;
  if (a.tick && !b.tick) {
    line = u < 0.5 ? { tick: a.tick * (1 - u * 2) } : { ...b, t1: lerp(0, b.t1 == null ? 1 : b.t1, (u - 0.5) * 2) };
  } else if (b.tick && !a.tick) {
    line = { ...b };
  } else {
    line = {
      ...(u < 0.5 ? a : b),
      lift: lerp(a.lift || 0, b.lift || 0, u),
      droop: lerp(a.droop || 0, b.droop || 0, u),
      curl: lerp(a.curl || 0, b.curl || 0, u),
      wave: lerp(a.wave || 0, b.wave || 0, u),
      alpha: lerp(a.alpha == null ? 1 : a.alpha, b.alpha == null ? 1 : b.alpha, u),
      t1: lerp(a.t1 == null ? 1 : a.t1, b.t1 == null ? 1 : b.t1, u),
    };
  }
  const fa = A.fx;
  const fb = B.fx;
  const fx = fa && fa.pebbles != null && !(fb && fb.pebbles != null) ? { ...fa, op: 1 - u } : u < 0.5 ? fa : fb;
  return {
    tx: lerp(A.tx || 0, B.tx || 0, u),
    ty: lerp(A.ty || 0, B.ty || 0, u),
    tint: lerp(A.tint || 0, B.tint || 0, u),
    glow: lerp(A.glow || 0, B.glow || 0, u),
    sx: lerp(A.sx == null ? 1 : A.sx, B.sx == null ? 1 : B.sx, u),
    sy: lerp(A.sy == null ? 1 : A.sy, B.sy == null ? 1 : B.sy, u),
    piv,
    rot,
    eyes,
    line,
    fx,
  };
}

/* ---------- the thirteen app states ---------- */

export type KiviStateId =
  | 'resting'
  | 'touch'
  | 'hello'
  | 'listening'
  | 'thinking'
  | 'speaking'
  | 'asking'
  | 'building'
  | 'done'
  | 'unsure'
  | 'cant_yet'
  | 'quiet'
  | 'silent';

/** Nothing open or running: calm lids, the line written, a slow breath of about 1%. */
export function kiviResting(t: number): KiviPose {
  'worklet';
  return breath(pose({ eyes: { mode: 'lid' } }), t);
}

/** A finger is down on Kivi: lids open, leaning toward the finger. */
export function kiviTouch(t: number): KiviPose {
  'worklet';
  return pose({ rot: 4, eyes: { mode: 'almond', look: [0.8, -0.6], open: blink(t, 3.6) }, line: { lift: 0.2 } });
}

/** Rises, holds, then settles with its lids closing before the next hello. */
export function kiviHello(t: number): KiviPose {
  'worklet';
  const c = t % 4.4;
  const o = c < 3.2 ? easeOut(c / 0.5) : 1 - easeIO((c - 3.2) / 0.6);
  return breath(pose({ ty: -3 * o, eyes: { mode: 'almond', open: o < 0.5 ? 0.1 : blink(t, 3.2, 0.9) }, line: { lift: 0.6 * o } }), t);
}

/**
 * Leans forward on its front point; the line follows the voice. level (0..1)
 * is the live voice level; without one, the page's own stand-in.
 */
export function kiviListening(t: number, level?: number): KiviPose {
  'worklet';
  const lv = level == null ? Math.max(0, sn(t, 0.9)) * (0.5 + 0.5 * Math.abs(sn(t, 3.1))) : clamp(level);
  return pose({ rot: 6 + lv * 1.5, eyes: { mode: 'almond', size: 1.08, open: blink(t, 3.6) }, line: { t1: 0.62 + 0.38 * lv } });
}

/** Leans back, looks up its line, three dots travel out, a soft glow. */
export function kiviThinking(t: number): KiviPose {
  'worklet';
  const P = breath(
    pose({
      rot: -3,
      piv: 'back',
      eyes: { mode: 'almond', h: 0.8, size: 0.88, look: [2.2, -2.8], open: blink(t, 5) },
      line: { t1: 0.3, dots: 1, dotP: t * 0.9 },
    }),
    t,
    0.3,
  );
  return { ...P, glow: 0.7 + 0.3 * Math.sin(TAU * 0.14 * t) };
}

/** The line waves with the words. */
export function kiviSpeaking(t: number): KiviPose {
  'worklet';
  return pose({ rot: 2, eyes: { mode: 'almond', h: 0.85, open: blink(t, 4) }, line: { w0: 5.8, wave: 1.5, waves: 1.8, phase: -t * 7 } });
}

/** Leans in and the line curls into a question. */
export function kiviAsking(t: number): KiviPose {
  'worklet';
  const n = Math.max(0, sn(t, 0.4, 1));
  return pose({ rot: 6 + n * 2, eyes: { mode: 'almond', look: [0.8, 1], open: blink(t, 4.4) }, line: { curl: 1, w: 0.8 } });
}

/** Steps aside at 84%; a record pebble lands for each record made, and they fade at the loop's end. */
export function kiviBuilding(t: number): KiviPose {
  'worklet';
  const c = t % 4.2;
  return pose({
    tx: -12,
    sx: 0.84,
    sy: 0.84,
    eyes: { mode: 'almond', look: [1.8, 1.6], h: 0.9, open: blink(t, 4.2, 2) },
    line: {},
    fx: { pebbles: clamp(c / 2.4) * 3, px: 0, op: c > 3.6 ? 1 - easeIO((c - 3.6) / 0.6) : 1 },
  });
}

/** The line bends into the tick, the eyes into arcs; it holds, then unwinds. */
export function kiviDone(t: number): KiviPose {
  'worklet';
  const c = t % 5.2;
  const p = c < 4.6 ? clamp((c - 0.3) / 0.5) : clamp(1 - (c - 4.6) / 0.5);
  const sq = c > 0.8 && c < 1 ? 1 - 0.02 * Math.sin((Math.PI * (c - 0.8)) / 0.2) : 1;
  return pose({ sy: sq, eyes: { mode: p > 0.2 ? 'arc' : 'almond' }, line: p > 0 ? { tick: easeIO(p) } : {} });
}

/** Glances side to side, one eye narrower, the line half curled. */
export function kiviUnsure(t: number): KiviPose {
  'worklet';
  const lk = sn(t, 0.35, 2.6);
  return pose({
    rot: -2,
    piv: 'back',
    eyes: { mode: 'almond', h: 0.9, openR: 0.7, look: [lk * 0.55, 0], open: blink(t, 4.8) },
    line: { t1: 0.72, curl: 0.6 },
  });
}

/** Leans back, looks down, a short lowered line. */
export function kiviCantYet(t: number): KiviPose {
  'worklet';
  return breath(
    pose({
      rot: -3,
      piv: 'back',
      eyes: { mode: 'almond', h: 0.6, tiltIn: 10, look: [0, 1.6], open: blink(t, 5.2, 1.3) },
      line: { droop: 0.6, t1: 0.62, w1: 3.8 },
    }),
    t,
    0.2,
  );
}

/** Muted: lids lowered, a short stub of line. */
export function kiviQuiet(_t: number): KiviPose {
  'worklet';
  return pose({ eyes: { mode: 'lid', heavy: 0.2 }, line: { t1: 0.3 } });
}

/** Do not disturb: lids closed, the line faded to a third. */
export function kiviSilent(t: number): KiviPose {
  'worklet';
  return breath(pose({ eyes: { mode: 'lid' }, line: { alpha: 0.35 } }), t, 0.16, 0.01);
}

/** A state's pose at t seconds. level is listening's live voice level. */
export function kiviPose(id: KiviStateId, t: number, level?: number): KiviPose {
  'worklet';
  switch (id) {
    case 'resting':
      return kiviResting(t);
    case 'touch':
      return kiviTouch(t);
    case 'hello':
      return kiviHello(t);
    case 'listening':
      return kiviListening(t, level);
    case 'thinking':
      return kiviThinking(t);
    case 'speaking':
      return kiviSpeaking(t);
    case 'asking':
      return kiviAsking(t);
    case 'building':
      return kiviBuilding(t);
    case 'done':
      return kiviDone(t);
    case 'unsure':
      return kiviUnsure(t);
    case 'cant_yet':
      return kiviCantYet(t);
    case 'quiet':
      return kiviQuiet(t);
    case 'silent':
      return kiviSilent(t);
  }
}

export type KiviState = {
  readonly id: KiviStateId;
  /** The time, s, of the frame held with Reduce Motion on. */
  readonly still: number;
  readonly pose: (t: number, level?: number) => KiviPose;
};

/** The thirteen app states, in the page's order, with their still times. */
export const KIVI_STATES: readonly KiviState[] = [
  { id: 'resting', still: 1.2, pose: kiviResting },
  { id: 'touch', still: 1, pose: kiviTouch },
  { id: 'hello', still: 2, pose: kiviHello },
  { id: 'listening', still: 1.1, pose: kiviListening },
  { id: 'thinking', still: 1.6, pose: kiviThinking },
  { id: 'speaking', still: 0.6, pose: kiviSpeaking },
  { id: 'asking', still: 1.2, pose: kiviAsking },
  { id: 'building', still: 3, pose: kiviBuilding },
  { id: 'done', still: 1.4, pose: kiviDone },
  { id: 'unsure', still: 1.2, pose: kiviUnsure },
  { id: 'cant_yet', still: 1, pose: kiviCantYet },
  { id: 'quiet', still: 0, pose: kiviQuiet },
  { id: 'silent', still: 1, pose: kiviSilent },
];

/** Each state's still time, for a worklet that cannot reach KIVI_STATES' functions. */
export function kiviStillOf(id: KiviStateId): number {
  'worklet';
  switch (id) {
    case 'resting':
    case 'asking':
    case 'unsure':
      return 1.2;
    case 'touch':
    case 'cant_yet':
    case 'silent':
      return 1;
    case 'hello':
      return 2;
    case 'listening':
      return 1.1;
    case 'thinking':
      return 1.6;
    case 'speaking':
      return 0.6;
    case 'building':
      return 3;
    case 'done':
      return 1.4;
    case 'quiet':
      return 0;
  }
}

/** Every state change blends over 320 ms on easeIO. */
export const KIVI_BLEND_S = 0.32;
/** The tick of done takes 500 ms. */
export const KIVI_TICK_S = 0.5;
/** After a call saves an entry, the floating button shows done this long, then rests. */
export const KIVI_DONE_S = 2.5;

/* ---------- sizes and sources ---------- */

/** How a Kivi of a drawn size is cut and lit. */
export type KiviSize = {
  readonly cut: KiviCut;
  readonly lit: boolean;
  /** The lit rim, on a dark scheme at 24 px and up. */
  readonly rim: boolean;
  /** Under 24 px the small cut is held still. */
  readonly still: boolean;
  /** The glow shows only at 40 px and up. */
  readonly glow: boolean;
};

/**
 * The cut for a drawn size, px: 60 and up the full cut, lit; 40-59 the button
 * cut, lit; 24-39 the small cut, flat; under 24 the small cut held still.
 */
export function kiviCutOf(px: number, scheme: 'light' | 'dark' = 'light'): KiviSize {
  'worklet';
  const rim = scheme === 'dark' && px >= 24;
  if (px >= 60) return { cut: 'full', lit: true, rim, still: false, glow: true };
  if (px >= 40) return { cut: 'button', lit: true, rim, still: false, glow: true };
  if (px >= 24) return { cut: 'small', lit: false, rim, still: false, glow: false };
  return { cut: 'small', lit: false, rim: false, still: true, glow: false };
}

/** The orb's modes. */
export type KiviOrbMode = 'idle' | 'touch' | 'listening' | 'thinking' | 'speaking' | 'quiet';

/** The orb's mode as a Kivi state, one to one. A live look with its halo (it needs a tap) shows asking. */
export function kiviStateOfOrbMode(mode: KiviOrbMode, halo = false): KiviStateId {
  'worklet';
  if (halo) return 'asking';
  return mode === 'idle' ? 'resting' : mode;
}

/** The pearl's looks. */
export type KiviPearlLook = 'pearl' | 'speaking' | 'working' | 'held';

/**
 * The pearl's look as a Kivi state. working is thinking without its glow;
 * held is thinking held on its still frame.
 */
export function kiviStateOfPearl(look: KiviPearlLook): { readonly id: KiviStateId; readonly glow: boolean; readonly held: boolean } {
  'worklet';
  if (look === 'speaking') return { id: 'speaking', glow: false, held: false };
  if (look === 'working') return { id: 'thinking', glow: false, held: false };
  if (look === 'held') return { id: 'thinking', glow: false, held: true };
  return { id: 'resting', glow: false, held: false };
}
